import React from 'react'
import { Button, ButtonProps, Tooltip } from '@mui/material'
import { usePermissions } from '../../contexts/PermissionContext'

interface PermissionButtonProps extends Omit<ButtonProps, 'action'> {
  permission?: string
  role?: string
  roles?: string[]
  resource?: string
  action?: string
  requireAll?: boolean
  disableTooltip?: string
  hideWhenNoAccess?: boolean
}

export const PermissionButton: React.FC<PermissionButtonProps> = ({
  children,
  permission,
  role,
  roles,
  resource,
  action,
  requireAll = false,
  disableTooltip = "You don't have permission to perform this action",
  hideWhenNoAccess = false,
  disabled,
  ...buttonProps
}) => {
  const permissions = usePermissions()

  const hasAccess = (): boolean => {
    const checks: boolean[] = []

    // Check specific permission
    if (permission) {
      checks.push(permissions.hasPermission(permission))
    }

    // Check resource + action permission
    if (resource && action) {
      checks.push(permissions.hasResourcePermission(resource, action))
    }

    // Check specific role
    if (role) {
      checks.push(permissions.hasRole(role))
    }

    // Check multiple roles
    if (roles && roles.length > 0) {
      checks.push(permissions.hasAnyRole(roles))
    }

    // If no checks specified, allow access
    if (checks.length === 0) {
      return true
    }

    // Return based on requireAll flag
    return requireAll ? checks.every(Boolean) : checks.some(Boolean)
  }

  const canAccess = hasAccess()

  if (hideWhenNoAccess && !canAccess) {
    return null
  }

  const button = (
    <Button
      {...buttonProps}
      disabled={disabled || !canAccess}
    >
      {children}
    </Button>
  )

  if (!canAccess && disableTooltip) {
    return (
      <Tooltip title={disableTooltip}>
        <span>{button}</span>
      </Tooltip>
    )
  }

  return button
}

// Convenience components
export const CreateButton: React.FC<Omit<PermissionButtonProps, 'resource' | 'action'> & { resource: string }> = ({
  resource,
  ...props
}) => (
  <PermissionButton resource={resource} action="create" {...props} />
)

export const EditButton: React.FC<Omit<PermissionButtonProps, 'resource' | 'action'> & { resource: string }> = ({
  resource,
  ...props
}) => (
  <PermissionButton resource={resource} action="update" {...props} />
)

export const DeleteButton: React.FC<Omit<PermissionButtonProps, 'resource' | 'action'> & { resource: string }> = ({
  resource,
  ...props
}) => (
  <PermissionButton resource={resource} action="delete" {...props} />
)

export const AdminButton: React.FC<Omit<PermissionButtonProps, 'roles'>> = (props) => (
  <PermissionButton roles={['super_admin', 'admin']} {...props} />
)